/**
 * Shared “low enrollment” rows for dashboard widgets (Admin / Staff / Teacher).
 * Teachers use class snapshot counts; Admin/Staff prefer enrollment rollups.
 */

import {
  type GetClassResponse,
  type GetEnrollmentResponse,
} from "@/openapi/generated";
import {
  isPendingEnrollment,
  occupiesClassSpot,
} from "@/lib/utils/enrollment";

/** Inclusive max enrolled count to list (product default: 5). */
export const DEFAULT_LOW_ENROLLMENT_THRESHOLD = 5;

export interface LowEnrollmentClassRow {
  classId: number;
  className: string;
  enrolled: number;
  maxCapacity: number;
  waitlist: number;
}

export interface LowEnrollmentBucket {
  enrollmentCount: number;
  classes: LowEnrollmentClassRow[];
}

/** Colors for pie segments / legend by enrollment count (0 = most urgent). */
export const LOW_ENROLLMENT_BUCKET_COLORS = [
  "#E53E3E",
  "#ED8936",
  "#F6AD55",
  "#ECC94B",
  "#68D391",
  "#4FD1C5",
] as const;

export function getLowEnrollmentBucketColor(enrollmentCount: number): string {
  return (
    LOW_ENROLLMENT_BUCKET_COLORS[enrollmentCount] ??
    LOW_ENROLLMENT_BUCKET_COLORS[LOW_ENROLLMENT_BUCKET_COLORS.length - 1]
  );
}

export function getLowEnrollmentBucketLabel(enrollmentCount: number): string {
  if (enrollmentCount === 0) return "No enrollments";
  if (enrollmentCount === 1) return "1 enrollment";
  return `${enrollmentCount} enrollments`;
}

export function getLowEnrollmentBucketTitle(enrollmentCount: number): string {
  if (enrollmentCount === 0) return "Classes with no enrollments";
  if (enrollmentCount === 1) return "Classes with 1 enrollment";
  return `Classes with ${enrollmentCount} enrollments`;
}

export function buildLowEnrollmentBuckets(
  items: readonly LowEnrollmentClassRow[],
  threshold = DEFAULT_LOW_ENROLLMENT_THRESHOLD,
): LowEnrollmentBucket[] {
  const grouped = new Map<number, LowEnrollmentClassRow[]>();

  for (let count = 0; count <= threshold; count++) {
    grouped.set(count, []);
  }

  for (const row of items) {
    if (row.enrolled >= 0 && row.enrolled <= threshold) {
      grouped.get(row.enrolled)!.push(row);
    }
  }

  return Array.from(grouped.entries()).map(([enrollmentCount, classes]) => ({
    enrollmentCount,
    classes: sortLowEnrollmentRows(classes),
  }));
}

function sortLowEnrollmentRows(rows: LowEnrollmentClassRow[]): LowEnrollmentClassRow[] {
  return [...rows].sort(
    (a, b) =>
      a.enrolled - b.enrolled ||
      a.className.localeCompare(b.className, undefined, { sensitivity: "base" }),
  );
}

function getEnrollmentClassId(enrollment: GetEnrollmentResponse): number | null {
  const classId = enrollment._class?.classId;
  if (classId == null) return null;
  const parsed = Number(classId);
  return Number.isFinite(parsed) ? parsed : null;
}

function countRosterEnrollmentsForClass(
  enrollments: readonly GetEnrollmentResponse[],
  classId: number,
): number {
  return enrollments.filter((enrollment) => {
    if (getEnrollmentClassId(enrollment) !== classId) return false;
    return occupiesClassSpot(enrollment) && !isPendingEnrollment(enrollment);
  }).length;
}

function countWaitlistEnrollmentsForClass(
  enrollments: readonly GetEnrollmentResponse[],
  classId: number,
): number {
  return enrollments.filter(
    (enrollment) =>
      getEnrollmentClassId(enrollment) === classId &&
      isPendingEnrollment(enrollment),
  ).length;
}

/**
 * Enrolled count matches class single page (`currentNumberOfStudents` from class API).
 * Falls back to roster enrollment rollups when the snapshot is unavailable.
 */
export function resolveClassEnrolledCount(
  classItem: Pick<GetClassResponse, "classId" | "currentNumberOfStudents">,
  enrollments: readonly GetEnrollmentResponse[],
): number {
  const classId = Number(classItem.classId);
  const snapshot = Number(classItem.currentNumberOfStudents ?? NaN);

  if (Number.isFinite(snapshot)) {
    return snapshot;
  }

  if (!Number.isFinite(classId)) return 0;
  return countRosterEnrollmentsForClass(enrollments, classId);
}

/**
 * Uses class snapshot counts for enrolled students; waitlist from enrollment rollups.
 */
export function computeLowEnrollmentFromClassesAndEnrollments(
  classes: readonly GetClassResponse[],
  enrollments: readonly GetEnrollmentResponse[],
  threshold = DEFAULT_LOW_ENROLLMENT_THRESHOLD,
): LowEnrollmentClassRow[] {
  const rows: LowEnrollmentClassRow[] = [];

  for (const classItem of classes) {
    const classId = Number(classItem.classId);
    if (!Number.isFinite(classId)) continue;

    const enrolledCount = resolveClassEnrolledCount(classItem, enrollments);
    const waitlistCount = countWaitlistEnrollmentsForClass(enrollments, classId);
    const maxCapacity = Number(classItem.maxNumberOfStudents ?? 20);

    rows.push({
      classId,
      className:
        typeof classItem.name === "string" && classItem.name.trim().length > 0
          ? classItem.name
          : "Unknown Class",
      enrolled: enrolledCount,
      maxCapacity,
      waitlist: waitlistCount,
    });
  }

  return sortLowEnrollmentRows(rows.filter((r) => r.enrolled <= threshold));
}

/**
 * Uses `currentNumberOfStudents` from class detail responses (Teacher dashboard;
 * avoids loading all enrollments). Waitlist is unknown → 0.
 */
export function computeLowEnrollmentFromClassSnapshots(
  classDetails: readonly GetClassResponse[],
  threshold = DEFAULT_LOW_ENROLLMENT_THRESHOLD,
): LowEnrollmentClassRow[] {
  const rows: LowEnrollmentClassRow[] = classDetails.map((classItem) => {
    const classId = Number(classItem.classId);
    const enrolledCount = Number(classItem.currentNumberOfStudents ?? 0);
    const maxCapacity = Number(classItem.maxNumberOfStudents ?? 20);
    return {
      classId,
      className:
        typeof classItem.name === "string" && classItem.name.trim().length > 0
          ? classItem.name
          : "Unknown Class",
      enrolled: enrolledCount,
      maxCapacity,
      waitlist: 0,
    };
  }).filter((r) => Number.isFinite(r.classId));

  return sortLowEnrollmentRows(rows.filter((r) => r.enrolled <= threshold));
}
